import { useMemo, useState } from "react";
import { Typography, Button, Card, CardHeader, CardContent, Spinner } from "@altimateai/lego";
import { IconSettings } from "@tabler/icons-react";
import ServicePrincipalConfigModal from "./ServicePrincipalConfigModal";
import { useFeatureFlag } from "@/helpers/useFeatureFlag";
import { isAdmin, isOwner } from "@/services/auth";
import { Stack } from "@altimateai/lego";
import { Bar, BarChart, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { DateRange } from "@/components/dateRange";
import dayjs from "dayjs";
import { useDatabricksUsers } from "../hooks";
import DatabricksUserList from "./DatabricksUserList";
import { PlaygroundUserViewEntities } from "@/modules/playground";
import { useAskAI } from "@/pages/sql-warehouses/hooks/useAskAI";
import { FlexListSummary } from "@/components/listSummary";
import { useAppState } from "@/modules/app/useAppContext";
import { formatCost } from "@/helpers/utils";
import { ChartInfoIcon } from "@/uiCore";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { DatatableContextProvider } from "@/components/tables/contexts/DatatableContextProvider";
import classes from "../../summary/summary.module.scss";

const CHART_COLORS = [
  "#2196F3",
  "#4CAF50",
  "#FF9800",
  "#9C27B0",
  "#F44336",
  "#00BCD4",
  "#795548",
  "#607D8B",
  "#E91E63",
  "#3F51B5",
];

const DatabricksUsers = () => {
  useBreadcrumb("Users");
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const enableGlobalPlayground = useFeatureFlag("gp");
  const { currency } = useAppState();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const canEditClassification = isOwner() || isAdmin();

  const formattedStartDate = useMemo(() => dayjs(startDate).format("YYYY-MM-DD"), [startDate]);
  const formattedEndDate = useMemo(
    () => dayjs(endDate || new Date()).format("YYYY-MM-DD"),
    [endDate]
  );

  const { handleAskAI } = useAskAI({
    resourceType: "user",
    resourceId: "all",
    resourceName: "Databricks Users",
    entityType: PlaygroundUserViewEntities.DATABRICKS_USER,
    context: { start_date: formattedStartDate, end_date: formattedEndDate },
  });

  // Fetch users data for summary and chart
  const { data: usersData, isLoading } = useDatabricksUsers({
    start_date: formattedStartDate,
    end_date: formattedEndDate,
    page_size: 100,
  });

  // Calculate summary statistics from the top page_size users.
  // Note: totalCost, totalQueries, and avgSuccessRate are computed from the
  // returned page (up to 100 users). A dedicated summary API endpoint would
  // be needed for globally accurate aggregates.
  const summaryData = useMemo(() => {
    if (!usersData?.users?.length) {
      return [
        { label: "Active Users", value: "0" },
        { label: "Total Query Cost", value: formatCost(0, currency) },
        { label: "Avg Success Rate", value: "N/A" },
        { label: "Total Queries", value: "0" },
      ];
    }

    const users = usersData.users;
    const totalCost = users.reduce((sum, u) => sum + (u.total_cost || 0), 0);
    const totalQueries = users.reduce((sum, u) => sum + u.total_queries, 0);
    const totalSuccessful = users.reduce((sum, u) => sum + u.successful_queries, 0);
    const avgSuccessRate = totalQueries > 0 ? (totalSuccessful / totalQueries) * 100 : 0;

    return [
      { label: "Active Users", value: String(usersData.total_count) },
      { label: "Total Query Cost", value: formatCost(totalCost, currency) },
      { label: "Avg Success Rate", value: `${avgSuccessRate.toFixed(1)}%` },
      { label: "Total Queries", value: totalQueries.toLocaleString() },
    ];
  }, [usersData, currency]);

  // Prepare bar chart data - top 10 users by cost
  const barChartData = useMemo(() => {
    if (!usersData?.users?.length) return [];

    const sortedUsers = [...usersData.users]
      .filter(u => u.total_cost > 0)
      .sort((a, b) => b.total_cost - a.total_cost)
      .slice(0, 10);

    return sortedUsers.map((user, index) => ({
      // Increase truncation limit from 25 to 35 chars for better readability
      name: user.user_name.length > 35 ? user.user_name.slice(0, 35) + "..." : user.user_name,
      fullName: user.user_name,
      cost: user.total_cost,
      queries: user.total_queries,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }));
  }, [usersData]);

  return (
    <DatatableContextProvider>
      <Stack direction="column" className={classes.summary}>
        <Stack className={classes.stickyHeader}>
          <Stack direction="row" align="center" justify="between" className="al-w-full al-px-4">
            <Typography variant="page-heading">Users</Typography>
            <Stack direction="row" align="center" gap="md">
              {enableGlobalPlayground && (
                <Button variant="outline" onClick={() => handleAskAI()}>
                  Ask AI
                </Button>
              )}
              <DateRange
                startDate={startDate}
                endDate={endDate}
                onDateRangeSelect={(start, end) => onUpdate(start, end)}
                id="users-date-range"
                maxDate={new Date()}
              />
              {canEditClassification && (
                <Button
                  variant="outline"
                  size="icon"
                  title="Classification settings"
                  onClick={() => setSettingsOpen(true)}
                >
                  <IconSettings size={16} />
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>

        <Stack direction="column" gap="xl" className="al-p-4">
          {/* Summary Cards */}
          <FlexListSummary isLoading={isLoading} summary={summaryData} />

          {/* Top Users by Query Cost - Horizontal Bar Chart */}
          <Card>
            <CardHeader>
              <Stack direction="row" align="center" gap="sm">
                <Typography variant="section-heading">Top Users by Query Cost</Typography>
                <ChartInfoIcon
                  title="How to read this chart"
                  content="This chart shows the top 10 users ranked by total query cost. Longer bars indicate higher spending. Hover over bars to see exact cost and query count."
                />
              </Stack>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Stack align="center" justify="center" className="al-h-[300px]">
                  <Spinner />
                </Stack>
              ) : barChartData.length === 0 ? (
                <Stack align="center" justify="center" className="al-h-[200px]">
                  <Typography className="al-text-gray-500">No cost data available</Typography>
                </Stack>
              ) : (
                <Stack style={{ width: "100%", height: Math.max(250, barChartData.length * 40) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barChartData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis type="number" tickFormatter={value => formatCost(value, currency)} />
                      <YAxis type="category" dataKey="name" width={240} tick={{ fontSize: 12 }} />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload || !payload.length) return null;
                          const data = payload[0].payload;
                          return (
                            <Stack
                              direction="column"
                              className="al-bg-white al-border al-p-2 al-rounded-md al-shadow-md"
                            >
                              <Typography weight="medium" className="al-mb-1">
                                {data.fullName}
                              </Typography>
                              <Stack direction="column" gap="xs">
                                <Typography size="sm">
                                  Cost: {formatCost(data.cost, currency)}
                                </Typography>
                                <Typography size="sm">
                                  Queries: {data.queries.toLocaleString()}
                                </Typography>
                              </Stack>
                            </Stack>
                          );
                        }}
                      />
                      <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
                        {barChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Users Table */}
          <DatabricksUserList startDate={formattedStartDate} endDate={formattedEndDate} />
        </Stack>
      </Stack>
      <ServicePrincipalConfigModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </DatatableContextProvider>
  );
};

export default DatabricksUsers;
